import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, ArrowRight } from 'lucide-react';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const { t } = useTranslation();

  const serviceContent = {
    'marine-construction': {
      title: t('home.services.marine.title'),
      description: t('home.services.marine.desc'),
      capabilities: [
        'Port and quay construction works',
        'Nearshore construction projects',
        'Vessel-assisted marine construction',
        'Coastal infrastructure development',
        'Marine logistics and coordination',
        'Heavy marine equipment operations'
      ]
    },
    'pipeline-piping': {
      title: t('home.services.pipeline.title'),
      description: t('home.services.pipeline.desc'),
      badge: t('home.services.pipeline.badge'),
      capabilities: [
        'Nearshore subsea pipeline construction',
        'Offshore pipeline installation',
        'Onshore pipeline systems',
        'Terminal piping infrastructure',
        'LNG infrastructure piping',
        'Industrial pipe system integration',
        'Pipeline welding and testing',
        'Subsea tie-in operations'
      ]
    },
    'offshore-operations': {
      title: t('home.services.offshore.title'),
      description: t('home.services.offshore.desc'),
      capabilities: [
        'Dredging operations',
        'Wreck removal services',
        'Heavy lifting operations',
        'Offshore construction support',
        'Marine vessel coordination',
        'Subsea operations',
        'ROV services coordination'
      ]
    },
    'energy-infrastructure': {
      title: t('home.services.energy.title'),
      description: t('home.services.energy.desc'),
      capabilities: [
        'LNG-to-Power solutions',
        'FSRU integration',
        'Terminal piping systems',
        'Storage tank infrastructure',
        'Fire-fighting line installation',
        'Foam line systems',
        'Cooling ring installations',
        'Power plant infrastructure',
        'Commissioning and start-up support'
      ]
    }
  };

  const service = serviceContent[serviceId];

  if (!service) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Service Not Found</h1>
          <Link to="/services" className="text-blue-900 hover:text-blue-700 font-semibold">
            Back to Services
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link
              to="/services"
              className="inline-flex items-center text-blue-200 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="mr-2" size={20} />
              Back to Services
            </Link>
            
            {service.badge && (
              <span className="inline-block px-4 py-2 bg-amber-500 text-slate-900 font-bold rounded-full text-sm mb-4">
                {service.badge}
              </span>
            )}
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {service.title}
            </h1>
            <p className="text-xl text-blue-200 max-w-3xl">
              {service.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service Details */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Capabilities */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-slate-900 mb-8">
                Key Capabilities
              </h2>
              <ul className="space-y-4">
                {service.capabilities.map((capability, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start space-x-3"
                  >
                    <CheckCircle className="text-amber-500 flex-shrink-0 mt-1" size={24} />
                    <span className="text-gray-700 text-lg">{capability}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* CTA Box */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-900 to-slate-900 text-white p-8 rounded-2xl shadow-2xl h-fit sticky top-24"
            >
              <h3 className="text-2xl font-bold mb-4">
                Interested in This Service?
              </h3>
              <p className="text-blue-200 mb-6">
                Get in touch with our team to discuss your project requirements and how we can help deliver your vision.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center w-full justify-center px-6 py-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-all"
              >
                {t('services.cta')} <ArrowRight className="ml-2" size={20} />
              </Link>

              {/* Quick Facts */}
              <div className="mt-8 pt-8 border-t border-slate-700">
                <h4 className="font-bold mb-4">Quick Facts</h4>
                <div className="space-y-3 text-sm text-blue-200">
                  <div>✓ 20+ years combined experience</div>
                  <div>✓ Africa & Middle East focus</div>
                  <div>✓ Local network advantage</div>
                  <div>✓ Proven EPC track record</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Projects CTA */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            See Our Work in Action
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Explore our portfolio of completed projects across marine and energy infrastructure.
          </p>
          <Link
            to="/projects"
            className="inline-flex items-center px-8 py-4 bg-blue-900 hover:bg-blue-800 text-white font-semibold rounded-lg transition-all"
          >
            View Projects <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
